'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { Comment } from '@/types/comment.types'
import { formatRelativeDate } from '@/lib/utils/format'
import { CommentForm } from './comment-form'

interface CommentThreadProps {
  comments: Comment[]
  onReply: (content: string, parentCommentId: string) => Promise<void>
  onDelete?: (commentId: string) => void
  currentUserId?: string
}

export function CommentThread({
  comments,
  onReply,
  onDelete,
  currentUserId,
}: CommentThreadProps) {
  if (comments.length === 0) {
    return null
  }

  return (
    <div className="space-y-4" role="feed" aria-label="Comments">
      <AnimatePresence initial={false}>
        {comments.map((comment) => (
          <motion.div
            key={comment.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <CommentItem
              comment={comment}
              onReply={onReply}
              onDelete={onDelete}
              currentUserId={currentUserId}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}

function CommentItem({
  comment,
  onReply,
  onDelete,
  currentUserId,
  depth = 0,
}: {
  comment: Comment
  onReply: (content: string, parentCommentId: string) => Promise<void>
  onDelete?: (commentId: string) => void
  currentUserId?: string
  depth?: number
}) {
  const [showReply, setShowReply] = useState(false)
  const [showReplies, setShowReplies] = useState(depth === 0)
  const isOwner = currentUserId === comment.user_id
  const hasReplies = comment.replies.length > 0

  return (
    <article className="group" aria-label={`Comment by ${comment.author_username}`}>
      <div className={`flex gap-3 ${depth > 0 ? 'ml-8 border-l-2 border-[var(--color-accent-border)] pl-4' : ''}`}>
        {/* Avatar */}
        <div
          className="h-8 w-8 shrink-0 rounded-full bg-[var(--color-bg-elevated)]"
          aria-hidden="true"
        >
          {comment.author_avatar_url ? (
            <img
              src={comment.author_avatar_url}
              alt=""
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-brand-indigo)]/20 text-[var(--text-xs)] font-medium text-[var(--color-brand-indigo)]">
              {comment.author_username.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          {/* Bubble */}
          <div className="rounded-[var(--radius-lg)] bg-[var(--color-bg-elevated)] px-3 py-2.5">
            {/* Header */}
            <div className="flex items-center gap-2">
              <span className="text-[var(--text-sm)] font-medium text-[var(--color-text-primary)]">
                @{comment.author_username}
              </span>
              <time
                dateTime={comment.created_at}
                className="text-[var(--text-xs)] text-[var(--color-text-muted)]"
              >
                {formatRelativeDate(comment.created_at)}
              </time>
            </div>

            {/* Content */}
            <p className="mt-1 text-[var(--text-sm)] text-[var(--color-text-secondary)] break-words">
              {comment.content}
            </p>
          </div>

          {/* Actions */}
          <div className="mt-1.5 flex items-center gap-3 pl-3">
            {depth < 1 && (
              <button
                type="button"
                onClick={() => setShowReply(!showReply)}
                className="text-[var(--text-xs)] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-brand-indigo)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
              >
                Reply
              </button>
            )}
            {isOwner && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(comment.id)}
                className="text-[var(--text-xs)] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-error)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
              >
                Delete
              </button>
            )}
          </div>

          {/* Reply form */}
          {showReply && depth < 1 && (
            <div className="mt-3">
              <CommentForm
                initialExpanded
                onSubmit={async (content) => {
                  await onReply(content, comment.id)
                  setShowReply(false)
                }}
                onCancel={() => setShowReply(false)}
              />
            </div>
          )}

          {/* Nested replies */}
          {hasReplies && (
            <div className="mt-3">
              {!showReplies && (
                <button
                  type="button"
                  onClick={() => setShowReplies(true)}
                  className="text-[var(--text-xs)] text-[var(--color-accent)] transition-colors hover:text-[var(--color-accent-light)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
                >
                  Show {comment.replies.length} more {comment.replies.length === 1 ? 'reply' : 'replies'}
                </button>
              )}
              {showReplies && (
                <div className="space-y-3">
                  {comment.replies.map((reply) => (
                    <CommentItem
                      key={reply.id}
                      comment={reply}
                      onReply={onReply}
                      onDelete={onDelete}
                      currentUserId={currentUserId}
                      depth={depth + 1}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  )
}
